import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Empty, Flex, Space, Spin, Typography } from 'antd';
import { CalendarOutlined, EditOutlined, RightOutlined, ThunderboltOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { history, useDispatch, useIntl, useSelector } from 'umi';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';

import { F } from '@/utils/intl';
import Drawer from '@/components/Drawer';
import PrettyDate from '@/components/PrettyDate';
import DoTask from '@/components/Lead/LeadTasks/DoTask';
import TaskActions from '@/components/Tasks/TaskActions';
import TaskAssignees from '@/components/Tasks/TaskAssignees';
import LeadComments from '@/components/Lead/LeadComments';
import { LeadActionTag } from '@/components/ActionTag';
import { useProjectDetails } from '@/components/ProjectDetails/useProjectDetails';
import {
  INACTIVE_SERVICE_REQUEST_STATUSES,
  processCalendarEvents,
  processServiceRequestsForCalendar,
} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import { doTaskActionRequest } from '@/services/api/taskAutomation';
import * as LeadTaskSelectors from '@/selectors/leadTaskSelectors';
import { LeadTaskModel, TaskAction } from '@/typings/models/LeadTask';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import { editLeadTaskRequest, partialEditLeadTaskRequest } from '@/services/api/leadTask';
import { runTaskUiConsequences } from '@/utils/taskUiConsequences';
import { pressableProps } from '@/utils/dom';
import { getTaskAssignees, getTaskLead, hasPermissionToEditTask, isLeadOwnedTask, isTaskSnoozed } from '@/utils/task';

import styles from './index.less';

const { Text, Title, Paragraph } = Typography;

/**
 * Task details drawer — "act immediately" layout: done-checkbox and ⋮ menu in
 * the header, the due/overdue status right below, then the task's action
 * buttons (equal size, the first — most likely — one filled, the rest
 * outlined), the description, and the lead context: project link, assignees,
 * upcoming appointments and the lead's comment thread.
 */
const TaskDetailsDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { taskId, toggledAt } = useSelector(LeadTaskSelectors.selectTaskDetailsDrawer) as {
    taskId?: string | null;
    toggledAt?: number | null;
  };
  const task = useSelector(
    (state: DefaultRootState) => LeadTaskSelectors.selectTaskDetails(state, taskId as string),
  ) as LeadTaskModel | null;
  const taskLoading = useSelector(
    (state: DefaultRootState) => state.loading.effects['leadTasks/fetchSingleTask'],
  );
  const commentPrefill = useSelector(
    (state: DefaultRootState) => (state as any).leadTasks?.commentPrefill,
  ) as { text: string; at?: number } | null;
  const [actionLoading, setActionLoading] = useState(false);
  const [editingDue, setEditingDue] = useState(false);
  const [editingAssignees, setEditingAssignees] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState<string[]>([]);

  const open = Boolean(taskId);
  const leadId = getTaskLead(task || undefined)?.id;
  const { leadData, refetch } = useProjectDetails(leadId as string);

  // The row payload (if any) renders instantly; the fetch upgrades it with the
  // detail-only data (full lead, description) and covers deep links
  useEffect(() => {
    if (taskId) dispatch({ type: 'leadTasks/fetchSingleTask', payload: { taskId } });
  }, [taskId]);

  // toggledAt re-fires this on every open, so appointments stay fresh even
  // when the same task is opened twice in a row
  useEffect(() => {
    if (leadId) dispatch({ type: 'leads/fetchSingleLead', payload: { leadId } });
  }, [leadId, toggledAt]);

  // Deep-linkable: the open drawer owns /tasks/:taskId (same pattern as CreateTaskDrawer)
  const previousRoute = useRef<string | null>(null);
  useEffect(() => {
    if (open && taskId) {
      if (!window.location.pathname.startsWith(`/tasks/${taskId}`)) {
        previousRoute.current = window.location.pathname;
        window.history.pushState({}, '', `/tasks/${taskId}`);
      }
    } else if (previousRoute.current) {
      if (previousRoute.current.match(/^\/tasks?\//)) return history.push('/tasks');
      window.history.pushState({}, '', previousRoute.current);
    }
  }, [open, taskId]);

  const handleClose = () => {
    dispatch({ type: 'leadTasks/closeTaskDetailsDrawer' });
  };

  const refreshTask = () => {
    if (taskId) dispatch({ type: 'leadTasks/fetchSingleTask', payload: { taskId } });
    // Keep the tasks list behind the drawer in sync with completions/snoozes
    dispatch({ type: 'leadTasks/fetch' });
    if (leadId) dispatch({ type: 'leads/fetchProjectTasks', payload: { leadId } });
    if (task?.related_item?.type === 'serviceRequest') {
      dispatch({ type: 'leadTasks/fetchAppointmentTasks', payload: { appointmentId: task.related_item.data?.id } });
    }
  };

  const saveDueDate = (value: any) => {
    setEditingDue(false);
    if (!task || !value) return;
    partialEditLeadTaskRequest({
      params: { taskId: task.id },
      data: { due_date: dayjs(value).startOf('day').format('YYYY-MM-DD[T]00:00:00Z') },
    }).finally(refreshTask);
  };

  const startEditingAssignees = () => {
    if (!task) return;
    setAssigneeDraft(getTaskAssignees(task).map((assignee) => assignee.id));
    setEditingAssignees(true);
  };

  // Assignee changes go through the same full PUT the edit form used — the API's partial
  // edit doesn't reassign owners. Picks stay local until the picker loses focus, then save once.
  const saveAssignees = (ids: string[]) => {
    setEditingAssignees(false);
    if (!task || ids.length === 0) return;
    const current = getTaskAssignees(task).map((assignee) => assignee.id);
    if (ids.length === current.length && ids.every((id) => current.includes(id))) return;
    editLeadTaskRequest({
      params: { taskId: task.id },
      data: {
        title: task.title,
        // Omitted keys stay unchanged; the list row only carries a truncated description_summary
        description: (task as any).description,
        due_date: task.due_date
          ? dayjs(task.due_date).format('YYYY-MM-DD[T]HH:mm:ssZ')
          : undefined,
        assignee_ids: ids,
      },
    }).finally(refreshTask);
  };

  const saveField = (field: 'title' | 'description', value: string) => {
    if (!task) return;
    const trimmed = value.trim();
    const current = field === 'title' ? task.title : description;
    if (!trimmed || trimmed === (current || '').trim()) return;
    partialEditLeadTaskRequest({ params: { taskId: task.id }, data: { [field]: trimmed } })
      .finally(refreshTask);
  };

  const runAction = (action: TaskAction) => {
    if (!task || task.is_completed) return;
    setActionLoading(true);
    doTaskActionRequest({ params: { taskId: task.id, actionId: action.uuid } })
      .then(() => {
        setActionLoading(false);
        refreshTask();
        refetch();
        // open_* consequences: the server no-ops them, the UI opens the surface
        runTaskUiConsequences(dispatch, task, action);
      })
      .catch(() => setActionLoading(false));
  };

  // Upcoming = active events starting from now, soonest first — unlike the
  // project drawer's recent-first slice, a task cares about what's next
  const upcomingEvents = React.useMemo(() => {
    if (isEmpty(leadData)) return [];
    return [
      ...processCalendarEvents(leadData.calendarEvents || []),
      ...processServiceRequestsForCalendar(leadData.service_requests || [])
        .filter((event: any) => !INACTIVE_SERVICE_REQUEST_STATUSES.includes(event.status)),
    ]
      .filter((event: any) => event.utc_start_time && dayjs(event.utc_start_time).isAfter(dayjs()))
      .sort((a: any, b: any) => (a.utc_start_time > b.utc_start_time ? 1 : -1));
  }, [leadData]);

  const siblingTasks = ((leadData?.tasks || []) as LeadTaskModel[]).filter(
    (sibling) => sibling.id !== task?.id,
  );

  const openAppointment = () => {
    const appointment = upcomingEvents[0];
    if (!appointment) return;
    if (appointment.blocker) {
      dispatch({
        type: 'serviceRequest/toggleCalendarEventDetailsDrawer',
        payload: { calendarEventId: appointment.id },
      });
      return;
    }
    dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
      payload: { appointmentId: appointment.id },
    });
  };
  const canEdit = task ? hasPermissionToEditTask(task) : false;
  const retired = Boolean(task?.retired_at);
  const snoozed = Boolean(task && !task.is_completed && !retired && isTaskSnoozed(task));
  const isOverdue = Boolean(task?.due_date && !task.is_completed && !retired && !snoozed && dayjs(task.due_date).isBefore(dayjs(), 'day'));
  const description = (task as any)?.description || task?.description_summary;
  const creatorName = (task?.creator as any)?.fullname || (task?.creator as any)?.name;
  const retireReason = task?.retire_reason
    ? intl.formatMessage({
        id: `taskAutomation.retire_trigger.${task.retire_reason}`,
        defaultMessage: task.retire_reason,
      })
    : null;

  return (
    <Drawer
      title={<F id="entity.task.singular" defaultMessage="Task" />}
      open={open}
      width={560}
      toggledAt={toggledAt}
      drawerBodyClassName={styles.drawerBody}
      onOpenChange={(nextOpen: boolean) => { if (!nextOpen) handleClose(); }}
      destroyOnHidden
    >
      <Spin spinning={Boolean(taskLoading) && !task} wrapperClassName={styles.drawerSpin}>
        {!task ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className={styles.inner}>
            {/* Header: done checkbox + title + created-by, ⋮ menu (edit/snooze/delete) */}
            <Flex align="flex-start" gap={8}>
              <DoTask task={task} updateRequest={refreshTask} />
              <div className={styles.headerText}>
                <Title
                  level={5}
                  className={styles.title}
                  editable={canEdit ? { onChange: (value) => saveField('title', value), triggerType: ['text', 'icon'] } : false}
                >
                  {task.title}
                </Title>
                {((task as any).created || creatorName) && (
                  <Text type="secondary" className={styles.createdLine}>
                    {(task as any).created && <PrettyDate date={(task as any).created} />}
                    {creatorName && <> · {creatorName}</>}
                  </Text>
                )}
                {/* Due status lives in the title column so the whole headline
                    (title / created / due) shares one left edge */}
                {retired ? (
                  <div className={classNames(styles.dueLine, styles.dueLineRetired)}>
                    <F id="pages.tasks.retired" defaultMessage="Retired" />
                  </div>
                ) : task.is_completed ? (
                  <div className={classNames(styles.dueLine, styles.dueLineDone)}>
                    <F id="pages.tasks.status.done" defaultMessage="Done" />
                  </div>
                ) : task.due_date && (
                  <div
                    className={classNames(styles.dueLine, {
                      [styles.dueLineOverdue]: isOverdue,
                      [styles.dueLineSnoozed]: snoozed,
                      [styles.dueLineEditable]: canEdit,
                    })}
                    onClick={() => canEdit && setEditingDue(true)}
                    {...(canEdit ? pressableProps(() => setEditingDue(true)) : {})}
                  >
                    <Space size={6}>
                      {canEdit && <CalendarOutlined className={styles.dueEditIcon} />}
                      {snoozed && task.snooze_until && (
                        <>
                          <F
                            id="pages.tasks.status.snoozedUntil"
                            defaultMessage="Snoozed until {date}"
                            values={{ date: dayjs(task.snooze_until).format('DD.MM.YYYY') }}
                          />
                          <span>·</span>
                        </>
                      )}
                      <F
                        id="pages.tasks.status.dueOn"
                        defaultMessage="Due {date}"
                        values={{ date: dayjs(task.due_date).format('DD.MM.YYYY') }}
                      />
                      <span>·</span>
                      {isOverdue ? (
                        <F
                          id="pages.tasks.status.overdueDays"
                          defaultMessage="{days} days overdue"
                          values={{ days: dayjs().startOf('day').diff(dayjs(task.due_date).startOf('day'), 'day') }}
                        />
                      ) : (
                        <PrettyDate date={task.due_date} />
                      )}
                    </Space>
                    {editingDue && (
                      <DatePicker
                        className={styles.duePicker}
                        open
                        autoFocus
                        value={dayjs(task.due_date)}
                        onChange={saveDueDate}
                        onOpenChange={(nextOpen) => { if (!nextOpen) setEditingDue(false); }}
                      />
                    )}
                  </div>
                )}
              </div>
              {/* actions stripped: the buttons live in the "next step" section below —
                  this keeps only the ⋮ menu (done/snooze/edit/delete). For retired
                  tasks the retirement facts live in the table below, not a chip. */}
              {!retired && <TaskActions task={{ ...task, actions: [] }} updateRequest={refreshTask} hideEdit />}
            </Flex>

            {/* Full-bleed divider: the headline above, everything else below */}
            <div className={styles.headerDivider} />

            {/* Next step: the design's bordered action list, one row per action */}
            {canEdit && !task.retired_at && (task.actions || []).length > 0 && (
              <div className={styles.actionList}>
                {(task.actions || []).map((action) => (
                  <div
                    key={action.uuid}
                    className={classNames(styles.actionRow, {
                      [styles.actionRowDisabled]: task.is_completed || actionLoading,
                    })}
                    onClick={() => runAction(action)}
                    {...pressableProps(() => runAction(action))}
                    aria-disabled={task.is_completed || actionLoading}
                  >
                    <ThunderboltOutlined className={styles.actionIcon} />
                    <span className={styles.actionLabel}>{action.button_label}</span>
                    <RightOutlined className={styles.actionChevron} />
                  </div>
                ))}
              </div>
            )}

            {/* Description — editable in place */}
            {(description || canEdit) && (
              <div className={styles.section}>
                <Text type="secondary" className={styles.sectionLabel}>
                  <F id="pages.tasks.description" defaultMessage="Description" />
                </Text>
                <Paragraph
                  className={styles.description}
                  editable={canEdit ? { onChange: (value) => saveField('description', value), triggerType: ['text', 'icon'] } : false}
                >
                  {description || ''}
                </Paragraph>
              </div>
            )}

            {/* Key facts: project, assignees, next appointment */}
            <div className={styles.factRows}>
              {/* Retirement facts as plain table rows — unmissable, no tooltip */}
              {retired && (
                <>
                  <div className={styles.factRow}>
                    <Text type="secondary" className={styles.factLabel}>
                      <F id="pages.tasks.retiredAt" defaultMessage="Retired at" />
                    </Text>
                    <span className={styles.factRetired}>
                      {dayjs(task.retired_at).format('DD.MM.YYYY')} · <PrettyDate date={task.retired_at} />
                    </span>
                  </div>
                  {retireReason && (
                    <div className={styles.factRow}>
                      <Text type="secondary" className={styles.factLabel}>
                        <F id="pages.tasks.retireReason" defaultMessage="Reason" />
                      </Text>
                      <span className={styles.factRetired}>{retireReason}</span>
                    </div>
                  )}
                </>
              )}
              {leadId && (
                <div className={styles.factRow}>
                  <Text type="secondary" className={styles.factLabel}>
                    <F id="entity.lead.singular" defaultMessage="Project" />
                  </Text>
                  <LeadActionTag lead={(isEmpty(leadData) ? getTaskLead(task) : leadData) as any} />
                </div>
              )}
              <div className={styles.factRow}>
                <Text type="secondary" className={styles.factLabel}>
                  <F id="pages.tasks.assignee" defaultMessage="Assignee" />
                </Text>
                {editingAssignees && canEdit && !isLeadOwnedTask(task) ? (
                  <span className={styles.assigneeEditor} onClick={(e) => e.stopPropagation()}>
                    <LeadAssigneeDropdownFilters
                      filterMode="select"
                      mode="multiple"
                      hideDisabled
                      value={assigneeDraft}
                      selectProps={{ autoFocus: true, defaultOpen: true, onBlur: () => saveAssignees(assigneeDraft) }}
                      onChange={(value: any) => setAssigneeDraft(Array.isArray(value) ? value : (value ? [value] : []))}
                    />
                  </span>
                ) : (
                  <Space size={4}>
                    <TaskAssignees task={task} />
                    {canEdit && !isLeadOwnedTask(task) && (
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        aria-label={intl.formatMessage({ id: 'common.actions.edit', defaultMessage: 'Edit' })}
                        onClick={startEditingAssignees}
                      />
                    )}
                  </Space>
                )}
              </div>
              {upcomingEvents[0] && (
                <div className={styles.factRow}>
                  <Text type="secondary" className={styles.factLabel}>
                    <F id="entity.appointment.singular" defaultMessage="Appointment" />
                  </Text>
                  <span
                    className={styles.factAppointment}
                    onClick={openAppointment}
                    {...pressableProps(openAppointment)}
                  >
                    {dayjs(upcomingEvents[0].utc_start_time).format('DD.MM. HH:mm')}
                    {upcomingEvents[0].title ? ` · ${upcomingEvents[0].title}` : ''}
                  </span>
                </div>
              )}
              {siblingTasks.length > 0 && (
                <div className={styles.factRowTop}>
                  <Text type="secondary" className={styles.factLabel}>
                    <F id="pages.tasks.drawer.more_tasks" defaultMessage="More tasks" />
                  </Text>
                  <div className={styles.siblingList}>
                    {siblingTasks.slice(0, 3).map((sibling) => (
                      <span
                        key={sibling.id}
                        className={classNames(styles.siblingLink, {
                          [styles.siblingDone]: sibling.is_completed,
                        })}
                        onClick={() => dispatch({ type: 'leadTasks/openTaskDetailsDrawer', payload: { task: sibling } })}
                        {...pressableProps(() => dispatch({ type: 'leadTasks/openTaskDetailsDrawer', payload: { task: sibling } }))}
                      >
                        <span className={styles.siblingTitle}>{sibling.title}</span>
                        <RightOutlined className={styles.siblingChevron} />
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Lead context: upcoming appointments + the lead's comment thread */}
            {leadId && (
              <>
                {/* Deliberately last and muted — reference material, not a call to action */}
                <div className={styles.commentsSection}>
                  <Text type="secondary" className={styles.sectionLabel}>
                    <F id="common.comments" defaultMessage="Comments" />
                  </Text>
                  <LeadComments
                    leadId={leadId}
                    leadDetails={leadData}
                    onPosted={refetch}
                    hideEmpty
                    prefill={commentPrefill}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Spin>
    </Drawer>
  );
};

export default TaskDetailsDrawer;
